import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, UsersThree, Package, CaretRight, Buildings } from '@phosphor-icons/react';
import ListSearch from '../../../components/ListSearch';
import { SkeletonBlock, EmptyState } from '../../../components/module-ui';
import { listAssetTeams, listCompanies, assignProductCompany } from '../api';

const UNASSIGNED = 'unassigned';

/**
 * Products that belong to one Company (or the virtual "Unassigned" bucket
 * when companyId === 'unassigned'). Clicking a product goes to the existing
 * Team Detail page — unchanged. The Unassigned view additionally offers an
 * inline "Assign to company" picker per product.
 */
function CompanyProductsPage() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [assigningId, setAssigningId] = useState(null);
  const [assignError, setAssignError] = useState(null);

  const isUnassigned = companyId === UNASSIGNED;

  const refresh = useCallback(() => {
    setLoading(true);
    Promise.all([listAssetTeams(), listCompanies()])
      .then(([allProducts, allCompanies]) => {
        setCompanies(allCompanies);
        setProducts(allProducts.filter((p) => (
          isUnassigned ? (p.product_id && !p.company_id) : p.company_id === companyId
        )));
        if (!isUnassigned) {
          setCompany(allCompanies.find((c) => c.company_id === companyId) || null);
        }
      })
      .catch((e) => console.error('Failed to fetch company products:', e))
      .finally(() => setLoading(false));
  }, [companyId, isUnassigned]);

  useEffect(() => { refresh(); }, [refresh]);

  const assignCompany = async (productId, targetCompanyId) => {
    if (!targetCompanyId) return;
    setAssigningId(productId);
    setAssignError(null);
    try {
      await assignProductCompany(productId, targetCompanyId);
      refresh();
    } catch (err) {
      setAssignError(err.response?.data?.detail || 'Failed to assign company');
    } finally {
      setAssigningId(null);
    }
  };

  const companyName = isUnassigned ? 'Unassigned' : (company?.name || 'Company');

  const q = query.trim().toLowerCase();
  const filtered = !q ? products : products.filter((t) => (
    (t.name || '').toLowerCase().includes(q)
    || (t.key || '').toLowerCase().includes(q)
    || (t.description || '').toLowerCase().includes(q)
  ));

  return (
    <div data-testid="company-products-view">
      <Link to="/dashboard/assets/companies" style={{
        display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px',
        color: '#002FA7', textDecoration: 'none', marginBottom: '8px', fontWeight: '600',
      }}>
        <ArrowLeft size={14} /> Companies
      </Link>

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '14px', marginBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Buildings size={16} />
          <p style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>{companyName}</p>
        </div>
        {company?.description && (
          <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>{company.description}</p>
        )}
        {isUnassigned && (
          <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
            Products with no company yet. Assign one below to move a product out of this list.
          </p>
        )}
      </div>

      {assignError && <p style={{ color: '#B91C1C', fontSize: '12px', marginBottom: '8px' }}>{assignError}</p>}

      <ListSearch
        value={query}
        onChange={setQuery}
        placeholder="Search products…"
        resultCount={filtered.length}
        totalCount={products.length}
      />

      {loading ? (
        <SkeletonBlock />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Package}
          title="No products found"
          description={query ? 'Try a different search.' : 'Assign products to this company from Product Management.'}
        />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '10px' }}>
          {filtered.map((t) => {
            const clickable = Boolean(t.team_id);
            return (
              <div
                key={t.team_id || t.product_id || t.name}
                data-testid="product-card"
                style={{
                  background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px',
                  padding: '14px', display: 'flex', flexDirection: 'column', gap: '8px',
                }}
              >
                <button
                  type="button"
                  disabled={!clickable}
                  onClick={() => clickable && navigate(`/dashboard/assets/teams/${t.team_id}`)}
                  style={{
                    background: 'none', border: 'none', padding: 0, textAlign: 'left',
                    cursor: clickable ? 'pointer' : 'default',
                    display: 'flex', flexDirection: 'column', gap: '8px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <p style={{ fontSize: '13px', fontWeight: '700', color: '#111827' }}>{t.name}</p>
                    {!t.active && (
                      <span style={{ fontSize: '11px', color: '#9CA3AF' }}>inactive</span>
                    )}
                    {clickable && <CaretRight size={14} color="#9CA3AF" style={{ marginLeft: 'auto' }} />}
                  </div>
                  {t.description && (
                    <p style={{ fontSize: '12px', color: '#6B7280' }}>{t.description}</p>
                  )}
                  <div style={{ display: 'flex', gap: '14px', fontSize: '12px', color: '#6B7280' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <UsersThree size={13} /> {t.member_count} member{t.member_count === 1 ? '' : 's'}
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <Package size={13} /> {t.assets_assigned} asset{t.assets_assigned === 1 ? '' : 's'}
                    </span>
                  </div>
                </button>
                {isUnassigned && (
                  <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: '8px' }}>
                    <select
                      value=""
                      disabled={assigningId === t.product_id}
                      onChange={(e) => assignCompany(t.product_id, e.target.value)}
                      className="input-field"
                      style={{ width: '100%', padding: '4px 6px', fontSize: '12px' }}
                      aria-label={`Assign ${t.name} to a company`}
                    >
                      <option value="">
                        {assigningId === t.product_id ? 'Assigning…' : 'Assign to company…'}
                      </option>
                      {companies.map((c) => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
                    </select>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default CompanyProductsPage;
